import { safeFetchAPI, SafeFetchResponse } from '../index'
import {
  MenuType,
  ReviewAuthTokenResponse,
  SendReviewCodeRequest,
  SendReviewCodeResponse,
  VerifyReviewCodeRequest,
} from '../../types/widget'
import { API_BASE } from './index'

type ReviewsAuthQueryParams = {
  storeID?: string
  menuType?: MenuType
}

function toQueryParams(input?: ReviewsAuthQueryParams): Record<string, string> {
  const params: Record<string, string> = {}
  if (input?.storeID) params.storeID = input.storeID
  if (input?.menuType) params.menuType = input.menuType
  return params
}

/**
 * `pii` is the raw phone or email the shopper typed. It goes into the URL
 * path. The server normalizes both the URL param and the JWT identity claim
 * before comparison (see reviewAuthIdentity in widgetapi), so the FE just
 * needs to send the same value on every subsequent request.
 */
function encodePii(pii: string): string {
  return encodeURIComponent(pii.trim())
}

/**
 * Request an OTP to be sent via SMS or email. Server picks the channel from
 * `payload.channel` when set, otherwise defaults to phone if the contact has
 * one else email. Rate limited: 1/sec, 5/min, 40/hr per IP, 30s cooldown per
 * destination.
 */
export async function sendReviewVerification(
  uid: string,
  pii: string,
  payload: SendReviewCodeRequest,
  queryParams?: ReviewsAuthQueryParams
): Promise<SafeFetchResponse<SendReviewCodeResponse>> {
  return safeFetchAPI<SendReviewCodeResponse>(
    `${API_BASE}/widget/reviews/auth/send/${uid}/${encodePii(pii)}`,
    {
      method: 'POST',
      payload,
      queryParams: toQueryParams(queryParams),
    }
  )
}

/**
 * Verify the OTP and receive a JWT that authorizes subsequent review calls.
 * JWT lifetime is 24 hours; refresh via `refreshReviewToken`.
 */
export async function verifyReviewCode(
  uid: string,
  pii: string,
  payload: VerifyReviewCodeRequest
): Promise<SafeFetchResponse<ReviewAuthTokenResponse>> {
  return safeFetchAPI<ReviewAuthTokenResponse>(
    `${API_BASE}/widget/reviews/auth/verify/${uid}/${encodePii(pii)}`,
    {
      method: 'POST',
      payload,
    }
  )
}

/**
 * Trade the current JWT for a fresh one. Server accepts the token via
 * `Authorization: Bearer <token>` and re-issues with the same claims.
 */
export async function refreshReviewToken(
  token: string
): Promise<SafeFetchResponse<ReviewAuthTokenResponse>> {
  return safeFetchAPI<ReviewAuthTokenResponse>(`${API_BASE}/widget/reviews/auth/refresh`, {
    method: 'POST',
    authToken: token,
  })
}
